type ValidationResult = {
    isValid: boolean;
    errorMessage: string | null;
}

type Validator = (value: string) => ValidationResult;

export const validateNoTitles = (accountName: string): ValidationResult => {
    if (!accountName) return { isValid: true, errorMessage: null };
    const trimmedName = accountName.trim();
    const titles = [
        'mr', 'mrs', 'ms', 'miss',
        'dr', 'prof', 'sir', 'madam',
        'master', 'rev', 'pastor', 'mister'
    ];

    for (const title of titles) {
        const pattern = new RegExp(`\\b${title}\\.?\\b`, 'i');

        if (pattern.test(trimmedName)) {
            const capitalizedTitle = title.charAt(0).toUpperCase() + title.slice(1);
            return {
                isValid: false,
                errorMessage: `Invalid account name: "${capitalizedTitle}" is not allowed`
            };
        }
    }

    return { isValid: true, errorMessage: null };
};

export const validateNoRepeatedNames = (accountName: string): ValidationResult => {
    if (!accountName) return { isValid: true, errorMessage: null };
    const trimmedName = accountName.trim();
    const nameParts = trimmedName.split(/\s+/).filter(part => part.length > 0);

    if (nameParts.length >= 2) {
        const firstName = nameParts[0].toLowerCase();
        const lastName = nameParts[nameParts.length - 1].toLowerCase();

        if (firstName === lastName) {
            return {
                isValid: false,
                errorMessage: `Invalid account name: First name and last name cannot be the same ("${nameParts[0]}")`
            };
        }

        for (let i = 0; i < nameParts.length - 1; i++) {
            if (nameParts[i].toLowerCase() === nameParts[i + 1].toLowerCase()) {
                return {
                    isValid: false,
                    errorMessage: `Invalid account name: Repeated name "${nameParts[i]}" is not allowed`
                };
            }
        }
    }

    return { isValid: true, errorMessage: null };
};

export const validateNoInitials = (accountName: string): ValidationResult => {
    if (!accountName) return { isValid: true, errorMessage: null };
    const trimmedName = accountName.trim();
    const nameParts = trimmedName.split(/\s+/).filter(part => part.length > 0);

    if (nameParts.length >= 2) {
        const firstName = nameParts[0];
        const lastName = nameParts[nameParts.length - 1];

        if (firstName.length === 1 || (firstName.length === 2 && firstName.endsWith('.'))) {
            return {
                isValid: false,
                errorMessage: "Invalid account name: First name cannot be an initial"
            };
        }

        if (lastName.length === 1 || (lastName.length === 2 && lastName.endsWith('.'))) {
            return {
                isValid: false,
                errorMessage: "Invalid account name: Last name cannot be an initial"
            };
        }
    }

    return { isValid: true, errorMessage: null };
};

export const validateRequired = (accountName: string): ValidationResult => {
    if (!accountName || accountName.trim().length === 0) {
        return { isValid: false, errorMessage: "Account name is required" };
    }
    return { isValid: true, errorMessage: null };
};

export const validateNoNumbers = (accountName: string): ValidationResult => {
    if (!accountName) return { isValid: true, errorMessage: null };
    if (/\d/.test(accountName)) {
        return {
            isValid: false,
            errorMessage: "Invalid account name: Numbers are not allowed"
        };
    }
    return { isValid: true, errorMessage: null };
};

export const validateMinLength = (minLength: number) => {
    return (accountName: string): ValidationResult => {
        if (!accountName) return { isValid: true, errorMessage: null };
        if (accountName.trim().length < minLength) {
            return {
                isValid: false,
                errorMessage: `Account name must be at least ${minLength} characters`
            };
        }
        return { isValid: true, errorMessage: null };
    };
};

export const combineValidators = (...validators: Validator[]) => {
    return (value: string): ValidationResult => {
        for (const validator of validators) {
            const result = validator(value);
            if (!result.isValid) {
                return result;
            }
        }
        return { isValid: true, errorMessage: null };
    };
};

export const validateAccountName = combineValidators(
    validateRequired,
    validateNoTitles,
    validateNoRepeatedNames,
    validateNoInitials,
    validateNoNumbers,
    validateMinLength(2)
)

export const accountNameValidator = (validator: Validator) => {
    return (value: string) => {
        const result = validator(value);
        return result.isValid ? true : result.errorMessage || "Invalid input";
    };
};